import { useEffect, useState } from "react";
import { X, Plus, Trash2, Save } from "lucide-react";

const Field = ({ label, children, testid }) => (
  <label className="block">
    <span className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">
      {label}
    </span>
    <div className="mt-1.5" data-testid={testid}>
      {children}
    </div>
  </label>
);

const inputCls =
  "w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF] outline-none transition-all";

export default function ReviewDialog({ open, data, onChange, onClose, onSave }) {
  const [local, setLocal] = useState(data);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocal(data);
  }, [data]);

  if (!open || !local) return null;

  const update = (patch) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange?.(next);
  };

  const updateItem = (idx, patch) => {
    const items = [...(local.items || [])];
    items[idx] = { ...items[idx], ...patch };
    update({ items });
  };

  const addItem = () => {
    update({
      items: [
        ...(local.items || []),
        { name: "", sku: "", quantity: 1, unit_price: 0.0 },
      ],
    });
  };

  const removeItem = (idx) => {
    const items = (local.items || []).filter((_, i) => i !== idx);
    update({ items });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        store_name: local.store_name || "",
        purchase_date: local.purchase_date || "",
        items: (local.items || []).map((it) => ({
          name: it.name || "",
          sku: it.sku || "",
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
        })),
        subtotal: Number(local.subtotal) || 0,
        tax: Number(local.tax) || 0,
        total: Number(local.total) || 0,
        currency: local.currency || "USD",
        receipt_id: local.receipt_id || "",
        storage_path: local.storage_path || null,
        original_filename: local.original_filename || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="review-dialog"
    >
      <div
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div>
            <div className="text-xs font-semibold tracking-wider uppercase text-[#0055FF]">
              Step 2 of 2
            </div>
            <h2 className="font-display font-bold text-xl text-gray-900 mt-1">
              Review &amp; edit receipt
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Parsed fields may be incomplete. Edit anything, then save.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="review-close"
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Store name" testid="field-store">
              <input
                className={inputCls}
                value={local.store_name || ""}
                onChange={(e) => update({ store_name: e.target.value })}
                placeholder="Rolex Boutique"
              />
            </Field>
            <Field label="Purchase date" testid="field-date">
              <input
                type="date"
                className={inputCls}
                value={local.purchase_date || ""}
                onChange={(e) => update({ purchase_date: e.target.value })}
              />
            </Field>
            <Field label="Receipt ID" testid="field-receipt-id">
              <input
                className={inputCls}
                value={local.receipt_id || ""}
                onChange={(e) => update({ receipt_id: e.target.value })}
                placeholder="RCP-10001"
              />
            </Field>
            <Field label="Currency" testid="field-currency">
              <input
                className={inputCls}
                value={local.currency || "USD"}
                onChange={(e) => update({ currency: e.target.value.toUpperCase() })}
                maxLength={4}
              />
            </Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">
                Items
              </span>
              <button
                onClick={addItem}
                data-testid="add-item-btn"
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#0055FF] hover:text-[#0044CC]"
              >
                <Plus size={12} /> Add item
              </button>
            </div>
            <div className="space-y-3">
              {(local.items || []).map((it, idx) => (
                <div
                  key={idx}
                  data-testid={`item-row-${idx}`}
                  className="grid grid-cols-12 gap-2 items-center bg-gray-50/60 border border-gray-200 rounded-lg p-3"
                >
                  <input
                    className={`${inputCls} col-span-5`}
                    value={it.name || ""}
                    onChange={(e) => updateItem(idx, { name: e.target.value })}
                    placeholder="Item name"
                  />
                  <input
                    className={`${inputCls} col-span-3 font-mono text-xs`}
                    value={it.sku || ""}
                    onChange={(e) => updateItem(idx, { sku: e.target.value })}
                    placeholder="SKU"
                  />
                  <input
                    type="number"
                    min="1"
                    className={`${inputCls} col-span-1`}
                    value={it.quantity || 1}
                    onChange={(e) =>
                      updateItem(idx, { quantity: Number(e.target.value) })
                    }
                  />
                  <input
                    type="number"
                    step="0.01"
                    className={`${inputCls} col-span-2 font-mono`}
                    value={it.unit_price ?? 0}
                    onChange={(e) =>
                      updateItem(idx, { unit_price: Number(e.target.value) })
                    }
                    placeholder="0.00"
                  />
                  <button
                    onClick={() => removeItem(idx)}
                    data-testid={`remove-item-${idx}`}
                    className="col-span-1 p-2 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 flex justify-center"
                    disabled={(local.items || []).length <= 1}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Subtotal" testid="field-subtotal">
              <input
                type="number"
                step="0.01"
                className={`${inputCls} font-mono`}
                value={local.subtotal ?? 0}
                onChange={(e) => update({ subtotal: Number(e.target.value) })}
              />
            </Field>
            <Field label="Tax" testid="field-tax">
              <input
                type="number"
                step="0.01"
                className={`${inputCls} font-mono`}
                value={local.tax ?? 0}
                onChange={(e) => update({ tax: Number(e.target.value) })}
              />
            </Field>
            <Field label="Total" testid="field-total">
              <input
                type="number"
                step="0.01"
                className={`${inputCls} font-mono`}
                value={local.total ?? 0}
                onChange={(e) => update({ total: Number(e.target.value) })}
              />
            </Field>
          </div>
        </div>

        <div className="border-t border-gray-200 p-4 flex items-center justify-end gap-2 bg-gray-50/50">
          <button
            onClick={onClose}
            data-testid="review-cancel"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="review-save"
            className="inline-flex items-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm hover:bg-[#0044CC] hover:-translate-y-0.5 transition-all disabled:opacity-60"
          >
            <Save size={14} />
            {saving ? "Saving…" : "Save receipt"}
          </button>
        </div>
      </div>
    </div>
  );
}
